import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { stepperSubtitleKeyMap } from '../shared/evaluationTypeLabelMap';
import { FormNavigator } from '../shared/FormNavigator';
import { RouteName } from '../types';

import type { Step } from '@/components/Stepper/StepItem';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    assignReviewerOnNextAtom,
    lastSavedAtAtom,
    questionnaireCreationOnNextAtom,
    scheduleCreationOnNextAtom,
    selectRevieweeOnNextAtom,
    selectTemplateOnNextAtom,
} from '@/atoms/evaluation360';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { CycleInstanceStatus } from '@/constants/review/CycleInstance';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { AssignReviewerPage } from '@/pages/Evaluation360/AssignReviewer/AssignReviewerPage';
import { QuestionnaireCreationPage } from '@/pages/Evaluation360/QuestionnaireCreation/QuestionnaireCreationPage';
import { ScheduleCreationPage } from '@/pages/Evaluation360/ScheduleCreation/ScheduleCreationPage';
import { SelectRevieweePage } from '@/pages/Evaluation360/SelectReviewee/SelectRevieweePage';
import { SelectTemplatePage } from '@/pages/Evaluation360/SelectTemplate/SelectTemplatePage';
import { SuccessMessageAction } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import {
    getInitialMaxStep,
    getStepFromPath,
    getStepPath,
    isTemplateStepCompleted,
} from '@/utils/cycleInstance';

export interface Evaluation360FormNavigatorParams {
    cycleInstanceId: string;
    step: string;
}
const ROUTES = [
    {
        path: `${RouteName.Eval360Form}/${getStepPath(CycleInstanceSteps.SELECT_REVIEWEE)}/:cycleInstanceId`,
        component: SelectRevieweePage,
    },
    {
        path: `${RouteName.Eval360Form}/${getStepPath(CycleInstanceSteps.ASSIGN_REVIEWER)}/:cycleInstanceId`,
        component: AssignReviewerPage,
    },
    {
        path: `${RouteName.Eval360Form}/${getStepPath(CycleInstanceSteps.SELECT_TEMPLATE)}/:cycleInstanceId`,
        component: SelectTemplatePage,
    },
    {
        path: `${RouteName.Eval360Form}/${getStepPath(CycleInstanceSteps.QUESTIONNAIRE_CREATION)}/:cycleInstanceId`,
        component: QuestionnaireCreationPage,
    },
    {
        path: `${RouteName.Eval360Form}/${getStepPath(CycleInstanceSteps.SCHEDULE_CREATION)}/:cycleInstanceId`,
        component: ScheduleCreationPage,
    },
];

export const Evaluation360FormNavigator = () => {
    const navigate = useNavigate();
    const { t } = useAmelioTranslations();
    const { cycleInstanceId, step } = useParams<Evaluation360FormNavigatorParams>();

    const { data: cycleInstance } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const selectRevieweeOnNext = useAtomValue(selectRevieweeOnNextAtom);
    const assignReviewerOnNext = useAtomValue(assignReviewerOnNextAtom);
    const selectTemplateOnNext = useAtomValue(selectTemplateOnNextAtom);
    const questionnaireCreationOnNext = useAtomValue(questionnaireCreationOnNextAtom);
    const scheduleCreationOnNext = useAtomValue(scheduleCreationOnNextAtom);
    const lastSavedAt = useAtomValue(lastSavedAtAtom);

    const allSteps: Step[] = [
        {
            id: CycleInstanceSteps.SELECT_REVIEWEE,
            label: t('evaluation360Stepper.selectReviewee.stepTitle'),
        },
        {
            id: CycleInstanceSteps.ASSIGN_REVIEWER,
            label: t('evaluation360Stepper.assignReviewer.stepTitle'),
        },
        {
            id: CycleInstanceSteps.SELECT_TEMPLATE,
            label: t('evaluation360Stepper.templateSelection.stepTitle'),
        },
        {
            id: CycleInstanceSteps.QUESTIONNAIRE_CREATION,
            label: t('evaluation360Stepper.questionnaireCreation.stepTitle'),
        },
        {
            id: CycleInstanceSteps.SCHEDULE_CREATION,
            label: t('evaluation360Stepper.scheduleCreation.stepTitle'),
        },
    ];

    // Keep the step while the user is on it (via "Change Template"); filtering it out then breaks FormNavigator's step lookup.
    const isOnSelectTemplateStep = getStepFromPath(step) === CycleInstanceSteps.SELECT_TEMPLATE;
    const steps =
        isTemplateStepCompleted(cycleInstance) && !isOnSelectTemplateStep
            ? allSteps.filter((s) => s.id !== CycleInstanceSteps.SELECT_TEMPLATE)
            : allSteps;

    const onNextCallbacks = useMemo(
        () => ({
            [CycleInstanceSteps.SELECT_REVIEWEE]: selectRevieweeOnNext,
            [CycleInstanceSteps.ASSIGN_REVIEWER]: assignReviewerOnNext,
            [CycleInstanceSteps.SELECT_TEMPLATE]: selectTemplateOnNext,
            [CycleInstanceSteps.QUESTIONNAIRE_CREATION]: questionnaireCreationOnNext,
            [CycleInstanceSteps.SCHEDULE_CREATION]: scheduleCreationOnNext,
        }),
        [
            selectRevieweeOnNext,
            assignReviewerOnNext,
            selectTemplateOnNext,
            questionnaireCreationOnNext,
            scheduleCreationOnNext,
        ],
    );

    const onSuccess = () => {
        navigate({
            path: RouteName.SuccessMessage,
            state: {
                title: t('evaluation360SuccessMessage.title'),
                buttonLabel: t('evaluation360SuccessMessage.goToDashboard'),
                actionType: SuccessMessageAction.NAVIGATE_TO_EVAL_DASHBOARD,
            },
        });
    };

    const initialMaxStep = getInitialMaxStep({ cycleInstance, steps });
    const isLaunched = cycleInstance?.status === CycleInstanceStatus.LAUNCHED;

    return (
        <FormNavigator
            steps={steps}
            routePrefix={RouteName.Eval360Form}
            urlParamName={'cycleInstanceId'}
            getStepFromPath={getStepFromPath}
            getStepPath={getStepPath}
            backRoute={RouteName.Evaluation360}
            onSuccess={onSuccess}
            onNextCallbacks={onNextCallbacks}
            lastSavedAt={lastSavedAt}
            stepperTitle={t('evaluation360Stepper.title')}
            stepperSubtitle={t(stepperSubtitleKeyMap.evaluation360)}
            stepperDataTestId={'360-evaluation-stepper'}
            routes={ROUTES}
            initialMaxStep={initialMaxStep}
            isLaunched={isLaunched}
        />
    );
};
